export interface UploadedFile {
  id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  /** Images show as thumbnails; files without it are listed by name. */
  is_image?: boolean;
}

export interface AttachmentRules {
  maxFiles: number;
  maxBytes: number;
  /** The file input's `accept`. */
  accept: string;
  /** Names and types worth uploading; the server has the last word. */
  acceptedNames: RegExp;
  acceptedTypes: readonly string[];
  /** Under the button, when there is room. */
  hint: string;
  tooLarge: string;
  wrongType: string;
  /** Whether a pasted screenshot is welcome (and the compact hint says so). */
  screenshots: boolean;
}

const TYPE_LABELS: Record<string, string> = {
  'application/pdf': 'PDF',
  'text/markdown': 'Markdown',
  'text/plain': 'Text',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint',
};

/** A file's type in words, from its content type. */
export function typeLabel(contentType: string): string {
  const known = TYPE_LABELS[contentType];
  if (known) return known;
  if (contentType.startsWith('image/')) return contentType.slice(6).toUpperCase();
  return 'File';
}

/** The same, from a file name, before the server has said what the bytes are. */
export function typeFromName(name: string): string {
  if (/\.pdf$/i.test(name)) return 'PDF';
  if (/\.(md|markdown)$/i.test(name)) return 'Markdown';
  if (/\.docx$/i.test(name)) return 'Word';
  if (/\.pptx$/i.test(name)) return 'PowerPoint';
  if (/\.txt$/i.test(name)) return 'Text';
  return 'File';
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** A screenshot from the clipboard is usually "image.png"; give it a name worth keeping. */
export function named(file: File): File {
  if (file.name && file.name !== 'image.png') return file;
  const stamp = new Date().toISOString().slice(0, 19).replace('T', ' ').replace(/:/g, '.');
  const extension = file.type.split('/')[1] ?? 'png';
  return new File([file], `Screenshot ${stamp}.${extension}`, { type: file.type });
}

/** Why a file is not worth uploading, or null when it is. */
export function fileProblem(file: File, rules: AttachmentRules): string | null {
  if (file.size > rules.maxBytes) return rules.tooLarge;
  if (!rules.acceptedNames.test(file.name) && !rules.acceptedTypes.includes(file.type)) return rules.wrongType;
  return null;
}
